<template>
  <div class="edit-profile-container">
    <div class="edit-profile-header">
      <h2>{{ $t('edit_profile.title') }}</h2>
      <button @click="goBack" class="back-btn">{{ $t('edit_profile.back') }}</button>
    </div>
    
    <div class="edit-profile-content">
      <!-- 编辑用户信息表单 -->
      <div class="edit-section">
        <h3>{{ $t('edit_profile.edit_user_info') }}</h3>
        <form @submit.prevent="saveUserInfo">
          <div class="form-group">
            <label>{{ $t('edit_profile.username') }}:</label>
            <input v-model="editUserInfo.username" type="text" required />
          </div>
          <div class="form-group">
            <label>{{ $t('edit_profile.email') }}:</label>
            <input v-model="editUserInfo.email" type="email" required />
          </div>
          <div class="form-group">
            <label>{{ $t('edit_profile.company') }}:</label>
            <input v-model="editUserInfo.company" type="text" />
          </div>
          <div class="form-actions">
            <button type="submit" class="save-btn">{{ $t('edit_profile.save') }}</button>
          </div>
        </form>
      </div>
      
      <!-- 修改密码表单 -->
      <div class="change-password-section">
        <h3>{{ $t('edit_profile.change_password') }}</h3>
        <form @submit.prevent="changePassword">
          <div class="form-group">
            <label>{{ $t('edit_profile.current_password') }}:</label>
            <input v-model="passwordData.currentPassword" type="password" required />
          </div>
          <div class="form-group">
            <label>{{ $t('edit_profile.new_password') }}:</label>
            <input v-model="passwordData.newPassword" type="password" required />
          </div>
          <div class="form-group">
            <label>{{ $t('edit_profile.confirm_password') }}:</label>
            <input v-model="passwordData.confirmPassword" type="password" required />
          </div>
          <div class="form-actions">
            <button type="submit" class="save-btn">{{ $t('edit_profile.save') }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { encryptUserData, decryptUserData } from '../utils/web-crypto';
import ModalService from '../ui_server/modalService';

export default {
  name: 'EditProfile',
  data() {
    return {
      editUserInfo: {
        username: '',
        email: '',
        company: ''
      },
      passwordData: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
    }
  },
  mounted() {
    this.loadUserProfile()
  },
  methods: {
    async loadUserProfile() {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          this.$router.push('/login')
          return
        }
        
        // 获取用户信息
        const userResponse = await axios.get('/api/user', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-client-id': 'web'
          }
        })
        if (userResponse.data.success) {
          // 解密用户信息
          const decryptedData = await decryptUserData(userResponse.data.encryptedData);
          
          this.editUserInfo = {
            username: decryptedData.un,
            email: decryptedData.e,
            company: decryptedData.c
          };
        } else {
          // 如果获取用户信息失败，跳转到登录页
          this.$router.push('/login')
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
        // 清除本地存储并跳转到登录页
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        localStorage.removeItem('licenseKey')
        this.$router.push('/login')
      }
    },
    goBack() {
      this.$router.go(-1)
    },
    async saveUserInfo() {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }
        
        // 准备更新数据
        const updateData = {
          u: this.editUserInfo.username,
          e: this.editUserInfo.email,
          c: this.editUserInfo.company
        };
        
        // 加密更新数据
        const encryptedUpdateData = await encryptUserData(updateData);
        
        // 发送更新请求
        const response = await axios.put('/api/user', { 
          encryptedData: encryptedUpdateData 
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-client-id': 'web',
            'Accept-Language': this.$i18n.locale
          }
        });
        
        if (response.data.success) {
          // 更新成功
          ModalService.showToast(this.$t('edit_profile.user_info_update_success'), 3000);
          // 更新本地存储的用户信息
          const userInfoStr = localStorage.getItem('userInfo');
          if (userInfoStr) {
            const userInfo = JSON.parse(userInfoStr);
            userInfo.un = this.editUserInfo.username;
            userInfo.e = this.editUserInfo.email;
            userInfo.c = this.editUserInfo.company;
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
          }
        } else {
          // 显示具体的错误信息
          ModalService.showError(this.$t('edit_profile.update_failed'), response.data.message);
        }
      } catch (error) {
        console.error('更新用户信息失败:', error);
        // 显示具体的错误信息
        if (error.response && error.response.data && error.response.data.message) {
          ModalService.showError(this.$t('edit_profile.update_failed'), error.response.data.message);
        } else {
          ModalService.showError(this.$t('edit_profile.update_failed_retry'), '');
        }
      }
    },
    async changePassword() {
      try {
        // 验证新密码和确认密码是否一致
        if (this.passwordData.newPassword !== this.passwordData.confirmPassword) {
          ModalService.showWarning(this.$t('edit_profile.password_mismatch'), '');
          return;
        }
        
        // 验证密码长度
        if (this.passwordData.newPassword.length < 6) {
          ModalService.showWarning(this.$t('edit_profile.password_length_min'), '');
          return;
        }
        
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }
        
        // 准备密码更新数据
        const passwordData = {
          cp: this.passwordData.currentPassword,
          np: this.passwordData.newPassword
        };
        
        // 加密密码数据
        const encryptedPasswordData = await encryptUserData(passwordData);
        
        // 发送密码更新请求
        const response = await axios.put('/api/user/password', { 
          encryptedData: encryptedPasswordData 
        }, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-client-id': 'web',
            'Accept-Language': this.$i18n.locale
          }
        });
        
        if (response.data.success) {
          // 更新成功
          ModalService.showToast(this.$t('edit_profile.password_update_success'), 3000);
          // 清空密码输入框
          this.passwordData = {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          };
        } else {
          // 显示具体的错误信息
          ModalService.showError(this.$t('edit_profile.password_update_failed'), response.data.message);
        }
      } catch (error) {
        console.error('修改密码失败:', error);
        // 显示具体的错误信息
        if (error.response && error.response.data && error.response.data.message) {
          ModalService.showError(this.$t('edit_profile.password_update_failed'), error.response.data.message);
        } else {
          ModalService.showError(this.$t('edit_profile.password_update_failed_retry'), '');
        }
      }
    }
  }
}
</script>

<style scoped>
.edit-profile-container {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.edit-profile-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.edit-profile-header h2 {
  margin: 0;
  color: #333;
}

.back-btn {
  padding: 10px 20px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.back-btn:hover {
  background-color: #5a6268;
}

.edit-profile-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.edit-section, .change-password-section {
  margin-bottom: 30px;
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.edit-section h3, .change-password-section h3 {
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #555;
}

.form-group input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
}

.save-btn {
  padding: 10px 20px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.save-btn:hover {
  background-color: #218838;
}

@media (max-width: 768px) {
  .edit-profile-header {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
}
</style>
